<template>
  <section class="profile-selector">
    <Dropdown direction="top" :is-visible="isDropdownVisible" :control-from-parent="true" @on-close="closeDropdown">
      <ProfileSelectorToggle
        :type="type"
        :with-avatar="withAvatar"
        :is-loading="isLoading"
        :is-disabled="isDisabled"
        :name="currentProfile.slug"
        :avatar-url="currentProfile.avatarURL"
        :size="toggleSize"
        :hint="hint"
        @click.native="toggleDropdown"
      >
        <BravadoText
          v-if="isNewLabelShown"
          class="profile-selector__new"
          tag="p"
          variant="caption3-semibold"
          @click.native.stop="onTipsShown"
        >
          New
        </BravadoText>
      </ProfileSelectorToggle>
      <DropdownMenu v-if="!isLoading && !isDisabled" slot="menu" class="profile-selector__dropdown">
        <ul class="profile-selector__dropdown-list">
          <li class="profile-selector__dropdown-item">
            <ProfileSelectorOption :type="type" :profile="publicProfile" @click.native="onSelectAnonymous(false)" />
          </li>
          <li v-if="hasAnonymousProfile" class="profile-selector__dropdown--item">
            <ProfileSelectorOption
              data-test="profile-selector-anonymous"
              :type="type"
              :profile="anonymousProfile"
              @click.native="onSelectAnonymous(true)"
            />
          </li>
          <li v-else class="profile-selector__dropdown--item">
            <ProfileSelectorOption :type="type" @click.native="onCreateProfile" />
          </li>
        </ul>
        <ProfileSelectorHelp class="js-prevent-handle-outside" :type="type" @on-show-tips="onTipsShown" />
      </DropdownMenu>
    </Dropdown>
    <AnonymityLearnMoreModal @on-submit="toggleDropdown" />
    <WarRoomUsernameAndAvatarModal @on-submit="onSelectAnonymous(true)" />
  </section>
</template>

<script lang="ts">
import { defineComponent } from '@nuxtjs/composition-api';
import AnonymityLearnMoreModal from '@/components/partials/optional-anonymity/AnonymityLearnMoreModal/AnonymityLearnMoreModal.vue';
import Dropdown, { DropdownMenu } from '@/components/common/Dropdown';
import ProfileSelectorToggle from '@/components/partials/optional-anonymity/ProfileSelector/ProfileSelectorToggle.vue';
import ProfileSelectorMixin from '@/components/partials/optional-anonymity/ProfileSelector/ProfileSelectorMixin';
import { SelectorType } from '@/components/partials/optional-anonymity/ProfileSelector/types';
import { Activity } from '@/types/graphql';

export default defineComponent({
  name: 'ProfileSelector',
  components: {
    AnonymityLearnMoreModal,
    Dropdown,
    DropdownMenu,
    ProfileSelectorToggle,
    ProfileSelectorOption: () =>
      import('@/components/partials/optional-anonymity/ProfileSelector/ProfileSelectorOption.vue'),
    ProfileSelectorHelp: () =>
      import('@/components/partials/optional-anonymity/ProfileSelector/ProfileSelectorHelp.vue'),
    WarRoomUsernameAndAvatarModal: () =>
      import('@/components/partials/war-room/WarRoomSignUp/WarRoomUsernameAndAvatarModal.vue'),
  },
  mixins: [ProfileSelectorMixin],
  props: {
    withAvatar: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isAnonymous: {
      type: Boolean,
      default: null,
    },
    hint: {
      type: String,
      default: '',
    },
  },
  computed: {
    toggleSize() {
      return this.type === SelectorType.comment ? 'sm' : 'md';
    },
    editProfileType(): Activity | null {
      if (this.isAnonymous !== null) {
        return this.isAnonymous ? Activity.Anonymous : Activity.Public;
      }
      return null;
    },
  },
});
</script>

<style lang="scss" scoped>
.profile-selector {
  display: inline-flex;

  &__new {
    padding: 4px;
    border-radius: 4px;
    background-color: var(--theme-color-cyan-light);
    color: var(--blue-60);

    &:hover,
    &:focus {
      background-color: var(--blue-60);
      color: var(--cyan-20);
    }
  }

  &__dropdown {
    min-width: 298px;
    margin-bottom: 4px;

    &-list {
      padding: 0;
      list-style: none;
    }

    &-item {
      &:not(:last-child) {
        border-bottom: 1px solid var(--theme-color-border);
      }
    }
  }
}
</style>
